"use client";

/**
 * NavigationBlocker asks "Leave page?" before the user loses something by navigating away:
 * Create All or an unsaved lightbox draft on the canvas (with `projectId`), or unsent chat text
 * on the landing page (without).
 * - beforeunload covers tab close, refresh and other-origin links; its text is ignored by browsers.
 * - popstate covers back/forward via a marker history entry, popped only when blocking turns off
 *   while mounted. Popping on unmount would send the user back from the page they just reached.
 * - Captured link clicks and `confirmNavigation` cover in-app navigation.
 * - After "Leave anyway", no guard fires again, so the user is never asked twice.
 * - Leave anyway replays the click on the intercepted anchor rather than navigating by href,
 *   so the link's own onClick (analytics, a Next `<Link>`'s client routing) still runs.
 */
import { useEffect, useRef, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import { Button } from "@/components/atoms/primitives/Button";
import { AlertTriangle } from "lucide-react";

export interface NavigationBlockerHandle {
  /**
   * Check if navigation should proceed. If blocking is active, shows confirmation modal.
   * Returns true if navigation should proceed, false if blocked.
   * Use for programmatic navigation (router.push, etc.)
   */
  confirmNavigation: (href: string) => boolean;
}

export interface NavigationBlockerProps {
  /** Whether blocking is active */
  isBlocking: boolean;
  /**
   * With a project ID, only navigation out of that project is blocked. Without one, any
   * same-origin navigation that changes the path or query is blocked.
   */
  projectId?: string;
  /** Message shown in the confirmation modal and set as the beforeunload returnValue */
  message?: string;
  /** Called when user confirms they want to leave */
  onConfirmLeave?: () => void;
  /** Ref to expose confirmNavigation method for programmatic navigation */
  blockerRef?: React.RefObject<NavigationBlockerHandle | null>;
}

/** True when `url` stays inside the given project, or on the current page when there is none. */
function isSameScope(url: URL, projectId: string | undefined): boolean {
  if (projectId) {
    return url.pathname.startsWith(`/${projectId}/`) || url.pathname === `/${projectId}`;
  }
  return (
    url.pathname === window.location.pathname && url.search === window.location.search
  );
}

/**
 * Link clicks the browser does not turn into a same-tab navigation of this page, so there is
 * nothing to guard: new tabs and windows, downloads, and other origins (beforeunload has those).
 */
function isUnguardedLinkClick(e: MouseEvent, link: HTMLAnchorElement, url: URL): boolean {
  if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return true;
  const target = link.getAttribute("target");
  if (target && !["_self", "_top", "_parent"].includes(target)) return true;
  if (link.hasAttribute("download")) return true;
  return url.origin !== window.location.origin;
}

interface PendingNavigation {
  type: "link" | "beforeunload" | "back" | "programmatic";
  href?: string;
}

export function NavigationBlocker({
  isBlocking,
  projectId,
  message = "Create All is still running. If you leave now, it will be aborted and some content may not be generated.",
  onConfirmLeave,
  blockerRef,
}: NavigationBlockerProps) {
  const [showModal, setShowModal] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState<PendingNavigation | null>(
    null,
  );
  const stayButtonRef = useRef<HTMLButtonElement>(null);
  const leavingRef = useRef(false);
  const markerPushedRef = useRef(false);
  const selfBackPendingRef = useRef(false);
  // The anchor a click was intercepted on, replayed verbatim by handleLeave on "Leave anyway".
  const pendingLinkRef = useRef<HTMLAnchorElement | null>(null);

  // Expose confirmNavigation method for programmatic navigation
  useEffect(() => {
    if (!blockerRef) return;

    const handle: NavigationBlockerHandle = {
      confirmNavigation: (href: string) => {
        if (!isBlocking) return true;

        try {
          if (isSameScope(new URL(href, window.location.origin), projectId)) return true;
        } catch {
          /* benign: URL parsing may fail for invalid href; safe fallback is to block */
        }

        // Show confirmation modal
        setPendingNavigation({ type: "programmatic", href });
        setShowModal(true);
        return false;
      },
    };

    // Assign to ref
    (blockerRef as React.MutableRefObject<NavigationBlockerHandle | null>).current =
      handle;

    return () => {
      (blockerRef as React.MutableRefObject<NavigationBlockerHandle | null>).current =
        null;
    };
  }, [blockerRef, isBlocking, projectId]);

  // Handle beforeunload for browser navigation (refresh, close tab, external links)
  useEffect(() => {
    if (!isBlocking) return;

    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (leavingRef.current) return;
      e.preventDefault();
      // Modern browsers show their own text, but some still need returnValue set to prompt at all.
      e.returnValue = message;
      return e.returnValue;
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [isBlocking, message]);

  // Handle browser back/forward buttons via popstate
  useEffect(() => {
    if (!isBlocking) {
      // Runs only while mounted. The cleanup below must never pop the marker: on unmount the
      // user has usually already navigated, and back() would undo that navigation.
      if (markerPushedRef.current) {
        markerPushedRef.current = false;
        // A back() already in flight will pop the marker; a second one would carry the user
        // off the page. Blocking that tracks an unsaved draft toggles this fast.
        if (
          !leavingRef.current &&
          !selfBackPendingRef.current &&
          window.history.state?.navigationBlockerActive
        ) {
          selfBackPendingRef.current = true;
          window.history.back();
        }
      }
      return;
    }

    leavingRef.current = false;
    const currentPath = window.location.pathname + window.location.search;
    // The marker survives cleanup, so a StrictMode re-run or a quick re-block must not stack a second one.
    if (!window.history.state?.navigationBlockerActive) {
      // The marker is already gone, so our own back() has landed and no popstate of ours is owed.
      selfBackPendingRef.current = false;
      window.history.pushState({ navigationBlockerActive: true }, "", currentPath);
    }
    markerPushedRef.current = true;

    const handlePopState = () => {
      // history.go(-2) after "Leave anyway" fires popstate too; re-arming here would trap the user.
      if (leavingRef.current) return;
      window.history.pushState({ navigationBlockerActive: true }, "", currentPath);
      // Traversal is async: blocking can re-arm before our own back() lands, and its popstate
      // then arrives here. It popped the marker, so restore it, but the user pressed nothing.
      if (selfBackPendingRef.current) {
        selfBackPendingRef.current = false;
        return;
      }
      setPendingNavigation({ type: "back" });
      setShowModal(true);
    };

    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, [isBlocking]);

  // Handle SPA navigation by intercepting link clicks
  useEffect(() => {
    if (!isBlocking) return;

    const handleClick = (e: MouseEvent) => {
      // handleLeave replays the click on this same anchor; without this guard the replay
      // would re-enter here, get intercepted again, and never actually leave.
      if (leavingRef.current) return;

      const target = e.target as HTMLElement;
      const link = target.closest("a");

      if (!link?.href) return;

      try {
        const url = new URL(link.href, window.location.origin);
        if (isSameScope(url, projectId)) return;

        const targetPath = url.pathname;
        const shouldBlock = projectId
          ? targetPath === "/" ||
            targetPath.startsWith("/sign-") ||
            // Match other project IDs (assumes project IDs are cuid format)
            /^\/[a-z0-9]{20,}/.test(targetPath)
          : !isUnguardedLinkClick(e, link, url);

        if (shouldBlock) {
          e.preventDefault();
          e.stopPropagation();
          pendingLinkRef.current = link;
          setPendingNavigation({ type: "link", href: link.href });
          setShowModal(true);
        }
      } catch {
        /* benign: URL parsing may fail for invalid link.href; safe fallback is to let navigation proceed */
      }
    };

    // Use capture phase to intercept before other handlers
    document.addEventListener("click", handleClick, true);
    return () => document.removeEventListener("click", handleClick, true);
  }, [isBlocking, projectId]);

  // Focus trap when modal opens
  useEffect(() => {
    if (!showModal) return;

    const previouslyFocused = document.activeElement as HTMLElement | null;
    stayButtonRef.current?.focus();

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        setShowModal(false);
        setPendingNavigation(null);
      } else if (e.key === "Tab") {
        // Basic focus trap - keep focus within modal
        e.preventDefault();
        stayButtonRef.current?.focus();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      previouslyFocused?.focus?.();
    };
  }, [showModal]);

  const handleStay = useCallback(() => {
    setShowModal(false);
    setPendingNavigation(null);
  }, []);

  const handleLeave = useCallback(() => {
    leavingRef.current = true;
    setShowModal(false);
    onConfirmLeave?.();

    if (pendingNavigation?.type === "link") {
      const anchor = pendingLinkRef.current;
      // Click the real anchor instead of assigning href: that runs the link's own onClick
      // (analytics, a Next <Link>'s client routing) and does not force a full page load.
      // The anchor is only gone if the page removed it while the dialog was open, in which
      // case there is nothing left to click and href is the correct fallback.
      if (anchor?.isConnected) {
        anchor.click();
      } else if (pendingNavigation.href) {
        window.location.href = pendingNavigation.href;
      }
    } else if (pendingNavigation?.type === "programmatic" && pendingNavigation.href) {
      window.location.href = pendingNavigation.href;
    } else if (pendingNavigation?.type === "back") {
      // Go back twice: once to undo our pushed state, once for the actual back
      window.history.go(-2);
    }

    pendingLinkRef.current = null;
    setPendingNavigation(null);
  }, [pendingNavigation, onConfirmLeave]);

  if (!showModal) return null;

  // Ported to document.body: a `backdrop-filter` ancestor (e.g. the landing chat's
  // frosted-glass container) becomes the containing block for `position: fixed`
  // descendants, so an inline overlay would cover only that box instead of the page.
  return createPortal(
    <div
      role="alertdialog"
      aria-modal="true"
      aria-labelledby="nav-blocker-title"
      aria-describedby="nav-blocker-desc"
      className="fixed inset-0 z-modal-confirmation flex items-center justify-center bg-slate-900/50 px-4 py-10 backdrop-blur-sm"
      onMouseDown={(e) => {
        // Clicking backdrop dismisses modal (stays on page)
        if (e.target === e.currentTarget) handleStay();
      }}
    >
      <div className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl">
        <div className="mb-4 flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-amber-100">
            <AlertTriangle className="h-5 w-5 text-amber-600" />
          </div>
          <h2 id="nav-blocker-title" className="text-lg font-semibold text-neutral-900">
            Leave page?
          </h2>
        </div>

        <p id="nav-blocker-desc" className="mb-6 text-sm text-neutral-600">
          {message}
        </p>

        <div className="flex justify-end gap-3">
          <Button
            variant="secondary"
            onClick={handleLeave}
            className="text-neutral-700"
          >
            Leave anyway
          </Button>
          <Button ref={stayButtonRef} variant="default" onClick={handleStay}>
            Stay on page
          </Button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
